import type { ChannelSharedItem, PersonalItem } from "../../lib/libraries";
import { extractRecipeDetails } from "../../lib/libraries";
import type { ReactNode } from "react";

type Props = {
  items: ChannelSharedItem[];
  meUserId?: string | number | null;
  renderPreview: (item: PersonalItem) => ReactNode;
};

export default function SharedItemsShelf({ items, meUserId, renderPreview }: Props) {
  if (!items.length) return null;

  return (
    <div className="px-4 sm:px-6">
      <div className="mb-2 flex items-center gap-2">
        <div className="text-xs text-fg-3">Shared</div>
        <div className="h-px flex-1 bg-white/10" />
      </div>
      <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent snap-x snap-mandatory">
        {items.map((shared) => {
          const isRecipe = shared.item.type === "recipe";
          const recipeDetails = isRecipe ? extractRecipeDetails(shared.item) : null;
          const metaChips = shared.item.meta && Array.isArray(shared.item.meta.chips)
            ? shared.item.meta.chips.slice(0, 4).filter((chip): chip is string => typeof chip === "string")
            : [];
          if (isRecipe && recipeDetails) {
            return (
              <div key={shared.id} className="min-w-[240px] rounded-2xl border border-white/15 bg-black/40 overflow-hidden flex flex-col">
                {shared.item.imageUrl && (
                  <img src={shared.item.imageUrl} alt={shared.item.title} className="h-32 w-full object-cover border-b border-white/10" />
                )}
                <div className="p-3 flex flex-col gap-2">
                  <div className="text-meta text-fg-3">Recipe</div>
                  <div className="text-sm font-medium text-white truncate">{shared.item.title}</div>
                  {metaChips.length > 0 && (
                    <div className="flex flex-wrap gap-1 text-meta text-fg-2">
                      {metaChips.map((chip) => (
                        <span key={`${shared.id}-${chip}`} className="rounded-full border border-white/10 bg-black/30 px-2 py-0.5">
                          {chip}
                        </span>
                      ))}
                    </div>
                  )}
                  {recipeDetails?.tags && recipeDetails.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 text-meta text-fg-3">
                      {recipeDetails.tags.slice(0, 4).map((tag) => (
                        <span key={`${shared.id}-tag-${tag}`} className="rounded-full border border-white/10 px-2 py-0.5 bg-white/5">
                          #{tag}
                        </span>
                      ))}
                      {recipeDetails.tags.length > 4 && <span className="text-fg-3">+{recipeDetails.tags.length - 4}</span>}
                    </div>
                  )}
                  {recipeDetails?.ingredients && recipeDetails.ingredients.length > 0 && (
                    <div>
                      <div className="text-meta text-fg-3">Staples</div>
                      <ul className="mt-1 list-disc list-inside space-y-0.5 text-fg-2">
                        {recipeDetails.ingredients.slice(0, 4).map((ing, idx) => (
                          <li key={`recipe-ing-${shared.id}-${idx}`} className="truncate">
                            {ing}
                          </li>
                        ))}
                        {recipeDetails.ingredients.length > 4 && (
                          <li className="text-fg-3">+{recipeDetails.ingredients.length - 4} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {recipeDetails?.sourceUrl ? (
                    <a href={recipeDetails.sourceUrl} target="_blank" rel="noreferrer" className="text-xs text-emerald-300 hover:text-emerald-200">
                      View full recipe →
                    </a>
                  ) : recipeDetails?.description ? (
                    <p className="text-xs text-fg-2 whitespace-pre-wrap break-words">{recipeDetails.description}</p>
                  ) : null}
                  <div className="mt-auto text-meta text-fg-3">{shared.sharedByUserId === meUserId ? "Shared by you" : "Shared by teammate"}</div>
                </div>
              </div>
            );
          }
          return (
            <div key={shared.id} className="min-w-[220px] rounded-2xl border border-white/15 bg-black/40 p-3 text-left">
              <div className="text-meta text-fg-3">{shared.item.type}</div>
              <div className="text-sm font-medium text-white truncate">{shared.item.title}</div>
              {shared.item.description && <div className="text-xs text-fg-2 line-clamp-2">{shared.item.description}</div>}
              {renderPreview(shared.item as PersonalItem)}
              <div className="text-meta text-fg-3 mt-2">{shared.sharedByUserId === meUserId ? "Shared by you" : "Shared by teammate"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
